import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { EditorState } from 'draft-js';
import { IntlShape, useIntl } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import { z } from 'zod';
import { endpoints, getIDFromUrl, Url } from '@rossum/api-client';
import { EmailTemplate } from '@rossum/api-client/emailTemplates';
import { QueueAction } from '@rossum/api-client/schemas';
import { EmailRecipients } from '@rossum/components/email-recipients/EmailRecipients';
import { SuggesterIconButton } from '@rossum/components/variable-suggestions/SuggesterIconButton';
import { SuggestVariables } from '@rossum/components/variable-suggestions/SuggestVariables';
import { FieldLabel } from '@rossum/ui';
import { IconExternalLink, IconMail } from '@rossum/ui/icons/tabler';
import {
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  SvgIcon,
  TextField,
  useTheme,
} from '@rossum/ui/material';
import { WysiwygControl2 } from '../../../components/ReactHookForm/controls/WysiwygControl/WysiwygControl';
import { useApiClient } from '../../../lib/apiClient';
import { editorStateToHtml, htmlToContentState } from '../../../lib/draft';
import { getUnsafe } from '../../../lib/helpers';
import { isValidEmail } from '../../../utils/validation';
import { getFieldCompletions } from '../../formulas/getFieldCompletions';
import { baseSuggesterSections } from '../../formulas/suggesterSections';
import { handleInsert } from '../components/SuggestVariables/insertion-helpers';
import { createForm } from './createForm';
import { createHeader } from './createHeader';

const MAX_ENTRIES = 3;

const QUERY_KEY_EMAIL_TEMPLATES = 'emailTemplates';

// Copy pasted for now so that i don't have to refactor other components to use arrays
// TODO: how do we unify hooks being used across features?
const useEmailTemplates = (queues: Url[]) => {
  const api = useApiClient();
  return useQuery({
    queryKey: [QUERY_KEY_EMAIL_TEMPLATES, queues] as const,

    queryFn: () => {
      if (!queues.length) {
        return Promise.reject(
          new Error('Email templates: at least one queue expected.')
        );
      }
      return api.unpaginatedRequest(endpoints.emailTemplates.list)({
        queue: queues.map(getIDFromUrl),
      });
    },
    enabled: queues.length > 0,
  });
};

const getTemplateData = (
  templates: EmailTemplate[],
  value: z.TypeOf<ReturnType<typeof emailActionPayloadSchema>>
) => {
  const selectedTemplate =
    templates.find(t => t.url === value.email_template) ?? undefined;

  return selectedTemplate
    ? {
        to: selectedTemplate.to?.map(t => t.email) ?? [],
        cc: selectedTemplate.cc?.map(t => t.email) ?? [],
        bcc: selectedTemplate.bcc?.map(t => t.email) ?? [],
        subject: selectedTemplate.subject,
        body: selectedTemplate.message,
        selectedTemplate,
      }
    : {
        to: value.to ?? [],
        cc: value.cc ?? [],
        bcc: value.bcc ?? [],
        subject: value.subject ?? '',
        body: value.body ?? '',
        selectedTemplate: undefined,
      };
};

export const emailActionPayloadSchema = (intl?: IntlShape) =>
  z
    .object({
      email_template: z.string().nullable().optional(),
      to: z.array(z.string()).optional(),
      cc: z.array(z.string()).optional(),
      bcc: z.array(z.string()).optional(),
      body: z.string().optional(),
      subject: z.string().optional(),
      attach_document: z.boolean().optional().default(false),
    })
    .superRefine(({ email_template, subject, to, body }, refinementContext) => {
      // Hacky superRefine to handle the case when email_template is not selected
      // I wasn't able to make it work with union type (the form was not updating the fields)

      if (!email_template && !to) {
        refinementContext.addIssue({
          code: z.ZodIssueCode.custom,
          message: intl?.formatMessage({
            id: 'features.queueSettings.rules.actions.sendEmail.form.to.required',
          }),
          path: ['to'],
        });
      }

      if (!email_template && !subject) {
        refinementContext.addIssue({
          code: z.ZodIssueCode.custom,
          message: intl?.formatMessage({
            id: 'features.queueSettings.rules.actions.sendEmail.form.subject.required',
          }),
          path: ['subject'],
        });
      }

      if (!email_template && !body) {
        refinementContext.addIssue({
          code: z.ZodIssueCode.custom,
          message: intl?.formatMessage({
            id: 'features.queueSettings.rules.actions.sendEmail.form.body.required',
          }),
          path: ['body'],
        });
      }
    });

const EmailActionForm = createForm(
  ({ value, onChange, errors, schema }) => {
    const intl = useIntl();
    const theme = useTheme();

    const base = baseSuggesterSections(intl, {
      fields: getFieldCompletions(schema.content ?? []),
    });
    const suggesterSections = [
      base.email,
      base.annotation,
      base.document,
      base.fields,
    ];

    const { data: templates, isLoading: templatesLoading } = useEmailTemplates(
      schema.queues
    );

    const { selectedTemplate, ...computedValue } = useMemo(
      () => getTemplateData(templates ?? [], value),
      [templates, value]
    );

    const [editorState, setEditorState] = useState(() =>
      EditorState.createWithContent(htmlToContentState(computedValue.body))
    );

    const subjectInputRef = useRef<HTMLInputElement>(null);

    const handleTemplateChange = (templateUrl: string) => {
      const template = templates?.find(t => t.url === templateUrl);

      const content = htmlToContentState(template?.message ?? '');
      setEditorState(e => EditorState.push(e, content, 'change-block-data'));

      onChange({
        email_template: template?.url,
      });
    };

    return (
      <Stack spacing={2}>
        <FieldLabel
          layout="floating"
          label={intl.formatMessage({
            id: 'components.emailTemplateForm.chooseTemplate',
          })}
        >
          <TextField
            select
            size="small"
            disabled={templatesLoading}
            value={value.email_template ?? ''}
            label={intl.formatMessage({
              id: 'components.emailTemplateForm.chooseTemplate',
            })}
            onChange={e => handleTemplateChange(e.target.value)}
            slotProps={{
              input: {
                endAdornment: selectedTemplate ? (
                  <IconButton
                    component={RouterLink}
                    target="_blank"
                    to={{
                      pathname: `/queues/${getIDFromUrl(selectedTemplate.queue)}/settings/emails`,
                      search: new URLSearchParams({
                        emailTemplate: selectedTemplate.id.toString(),
                      }).toString(),
                    }}
                    size="small"
                    sx={{ mr: 3 }}
                  >
                    <SvgIcon fontSize="medium">
                      <IconExternalLink />
                    </SvgIcon>
                  </IconButton>
                ) : undefined,
              },
            }}
          >
            <MenuItem value="">
              {intl.formatMessage({
                id: 'components.emailResponseForm.noTemplate',
              })}
            </MenuItem>
            {templates?.map(template => (
              <MenuItem key={template.url} value={template.url}>
                {template.name}
              </MenuItem>
            ))}
          </TextField>
        </FieldLabel>

        {/* TODO: Do we want suggested recipient emails here? */}
        <EmailRecipients
          validate={(email: string) => {
            // Return true if the email is a text in braces (variable)
            if (email.startsWith('{') && email.endsWith('}')) {
              return true;
            }

            return isValidEmail(email);
          }}
          suggestedRecipientEmails={undefined}
          disabled={selectedTemplate !== undefined}
          createSuggestVariablesComponent={
            selectedTemplate === undefined
              ? (fieldType: 'to' | 'cc' | 'bcc') => (
                  <SuggestVariables
                    sections={suggesterSections}
                    mode="fstring"
                    onInsert={handleInsert({
                      mode: 'autocomplete',
                      currentValues: computedValue[fieldType],
                      onChange: newValues => {
                        onChange({ ...value, [fieldType]: newValues });
                      },
                    })}
                  >
                    <SuggesterIconButton />
                  </SuggestVariables>
                )
              : undefined
          }
          to={{
            field: {
              value: computedValue.to,
              onChange: v => onChange({ ...value, to: v }),
            },
            fieldState: {
              // @ts-expect-error
              error: getUnsafe(errors, 'to'),
            },
          }}
          cc={{
            field: {
              value: computedValue.cc,
              onChange: v => onChange({ ...value, cc: v }),
            },
          }}
          bcc={{
            field: {
              value: computedValue.bcc,
              onChange: v => onChange({ ...value, bcc: v }),
            },
          }}
        />
        <TextField
          label={intl.formatMessage({
            id: 'features.queueSettings.rules.actions.sendEmail.form.subject',
          })}
          size="small"
          value={computedValue.subject}
          onChange={e => onChange({ ...value, subject: e.currentTarget.value })}
          error={getUnsafe(errors, 'subject') !== undefined}
          helperText={getUnsafe(errors, 'subject.message')?.toString()}
          disabled={selectedTemplate !== undefined}
          inputRef={subjectInputRef}
          InputProps={{
            readOnly: selectedTemplate !== undefined,
            endAdornment: selectedTemplate === undefined && (
              <SuggestVariables
                sections={suggesterSections}
                mode="fstring"
                onInsert={handleInsert({
                  mode: 'textField',
                  inputRef: subjectInputRef,
                  currentValue: computedValue.subject,
                  onChange: newSubject =>
                    onChange({ ...value, subject: newSubject }),
                })}
              >
                <SuggesterIconButton />
              </SuggestVariables>
            ),
          }}
        />
        <WysiwygControl2
          onChange={newEditorState => {
            setEditorState(newEditorState);
            onChange({ ...value, body: editorStateToHtml(newEditorState) });
          }}
          value={editorState}
          disabled={selectedTemplate !== undefined}
          customButtons={[
            <SuggestVariables
              key="template-suggester"
              sections={suggesterSections}
              mode="fstring"
              onInsert={handleInsert({
                mode: 'wysiwyg',
                editorState,
                onChange: newEditorState => {
                  setEditorState(newEditorState);
                  onChange({
                    ...value,
                    body: editorStateToHtml(newEditorState),
                  });
                },
              })}
            >
              <SuggesterIconButton
                disabled={selectedTemplate !== undefined}
                svgColor={theme.palette.common.white}
              />
            </SuggestVariables>,
          ]}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={value.attach_document ?? false}
              onChange={e =>
                onChange({ ...value, attach_document: e.target.checked })
              }
            />
          }
          label={intl.formatMessage({
            id: 'features.queueSettings.rules.actions.sendEmail.form.attachDocument',
          })}
        />
      </Stack>
    );
  },
  intl => emailActionPayloadSchema(intl)
);

const EmailActionHeader = createHeader(
  ({ value, RenderComponent, schema }) => {
    const intl = useIntl();

    const { data: templates } = useEmailTemplates(schema.queues);

    const { selectedTemplate, ...computedValue } = useMemo(
      () => getTemplateData(templates ?? [], value),
      [templates, value]
    );

    return (
      <RenderComponent
        entries={[
          ...(selectedTemplate
            ? [
                {
                  key: 'template',
                  value: selectedTemplate.name,
                },
              ]
            : []),
          ...(computedValue.to.length
            ? computedValue.to
                .slice(0, MAX_ENTRIES)
                .map((to: string, index: number) => ({
                  key: `to-${index}`,
                  label: intl.formatMessage({
                    id: 'features.queueSettings.rules.actions.sendEmail.form.to',
                  }),
                  value: to,
                }))
            : []),
          ...(computedValue.to.length > MAX_ENTRIES
            ? [
                {
                  key: 'more_recipients',
                  value: `+${computedValue.to.length - MAX_ENTRIES}`,
                },
              ]
            : []),
          {
            key: 'subject',
            label: intl.formatMessage({
              id: 'features.queueSettings.rules.actions.sendEmail.form.subject',
            }),
            value: computedValue.subject,
            sx: {
              maxWidth: 400,
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            },
          },
        ]}
      />
    );
  },
  intl => emailActionPayloadSchema(intl)
);

export const emailAction = {
  icon: IconMail,
  intlKey: 'sendEmail' as const,
  groupIntlKey: 'messages' as const,
  resolveAction: (a: QueueAction) => a.type === 'send_email',
  serialized: { type: 'send_email' as const },
  form: EmailActionForm,
  fullWidth: true,
  header: EmailActionHeader,
  supportedEvents: [
    'annotation_confirmed',
    'annotation_imported',
    'annotation_import_succeeded',
    'annotation_import_failed',
    'annotation_exported',
    'annotation_export_succeeded',
    'annotation_export_failed',
  ] as const,
  payloadSchema: (intl?: IntlShape) => emailActionPayloadSchema(intl),
};
